// Offline storage utilities for handling data when internet is unavailable
class OfflineStorage {
  constructor() {
    this.STORAGE_KEYS = {
      PENDING_DATA: 'pending_offline_data',
      LAST_SYNC: 'last_sync_time',
      CONSENT_DATA: 'offline_consent_data',
      QUESTIONS_DATA: 'offline_questions_data',
      BARCODE_DATA: 'offline_barcode_data',
      ELIGIBILITY_DATA: 'offline_eligibility_data'
    };
  }

  // Store data locally when offline
  storeData(type, data) {
    try {
      const existingData = this.getStoredData(type);
      const newData = Array.isArray(existingData) ? [...existingData, data] : [data];
      
      localStorage.setItem(
        this.STORAGE_KEYS[type.toUpperCase() + '_DATA'], 
        JSON.stringify(newData)
      );
      
      // Also add to pending queue for sync
      this.addToPendingQueue({ type, data, timestamp: new Date().toISOString() });
      
      return true;
    } catch (error) {
      console.error('Error storing offline data:', error);
      return false;
    }
  }

  // Get stored data
  getStoredData(type) {
    try {
      const key = this.STORAGE_KEYS[type.toUpperCase() + '_DATA'];
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error retrieving offline data:', error);
      return [];
    }
  }

  // Add to pending sync queue
  addToPendingQueue(item) {
    try {
      const pending = this.getPendingQueue();
      pending.push(item);
      localStorage.setItem(this.STORAGE_KEYS.PENDING_DATA, JSON.stringify(pending));
    } catch (error) {
      console.error('Error adding to pending queue:', error);
    }
  }

  // Get pending sync queue
  getPendingQueue() {
    try {
      const pending = localStorage.getItem(this.STORAGE_KEYS.PENDING_DATA);
      return pending ? JSON.parse(pending) : [];
    } catch (error) {
      console.error('Error retrieving pending queue:', error);
      return [];
    }
  }

  // Clear pending queue after successful sync
  clearPendingQueue() {
    try {
      localStorage.removeItem(this.STORAGE_KEYS.PENDING_DATA);
      localStorage.setItem(this.STORAGE_KEYS.LAST_SYNC, new Date().toISOString());
      return true;
    } catch (error) {
      console.error('Error clearing pending queue:', error);
      return false;
    }
  }

  // Check if we have pending data to sync
  hasPendingData() {
    const pending = this.getPendingQueue();
    return pending.length > 0;
  }

  // Get last sync time
  getLastSyncTime() {
    return localStorage.getItem(this.STORAGE_KEYS.LAST_SYNC);
  }

  // Check if device is online
  isOnline() {
    return navigator.onLine;
  }

  // Sync pending data with backend
  async syncPendingData() {
    if (!this.isOnline()) {
      console.log('Device is offline, cannot sync');
      return { success: false, message: 'Device is offline' };
    }

    const pending = this.getPendingQueue();
    if (pending.length === 0) {
      return { success: true, message: 'No pending data to sync' };
    }

    const results = {
      success: 0,
      failed: 0,
      errors: []
    };

    for (const item of pending) {
      try {
        const endpoint = this.getEndpointForType(item.type);
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(item.data)
        });

        if (response.ok) {
          results.success++;
        } else {
          results.failed++;
          results.errors.push(`Failed to sync ${item.type}: ${response.statusText}`);
        }
      } catch (error) {
        results.failed++;
        results.errors.push(`Error syncing ${item.type}: ${error.message}`);
      }
    }

    if (results.success === pending.length) {
      this.clearPendingQueue();
    }

    return results;
  }

  // Get appropriate endpoint for data type
  getEndpointForType(type) {
    const endpoints = {
      consent: '/api/consent',
      questions: '/api/questions',
      barcode: '/api/barcode',
      'not-eligible': '/api/not-eligible',
      'make-eligible': '/api/make-eligible'
    };
    
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    return `${baseUrl}${endpoints[type] || '/api/data'}`;
  }

  // Clear all offline data (for logout or reset)
  clearAllOfflineData() {
    try {
      Object.values(this.STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      return true;
    } catch (error) {
      console.error('Error clearing offline data:', error);
      return false;
    }
  }
}

export default new OfflineStorage();
